import type { AthenaPrincipal } from "../../../runtime/data/principal.ts";
// Preflight lives in execute/invoke.ts: prepareBillingCommand (src/billing/safety).
import {
	AthenaBillingCapabilityError,
	isAthenaBillingCredentialError,
} from "../../errors.ts";
import type {
	BillingCancelPaymentInput,
	BillingCancelRefundInput,
	BillingCancelSubscriptionInput,
	BillingCreateCustomerInput,
	BillingCreatePaymentInput,
	BillingCreatePaymentLinkInput,
	BillingCreateRefundInput,
	BillingCreateSubscriptionInput,
	BillingDeleteCustomerInput,
	BillingDeletePaymentLinkInput,
	BillingExecutionTarget,
	BillingGetCustomerInput,
	BillingGetInvoiceInput,
	BillingGetPaymentInput,
	BillingGetPaymentLinkInput,
	BillingGetRefundInput,
	BillingGetSubscriptionInput,
	BillingListCustomersInput,
	BillingListInvoicesInput,
	BillingListPaymentLinksInput,
	BillingListPaymentsInput,
	BillingListRefundsInput,
	BillingListSubscriptionsInput,
	BillingPayment,
	BillingUpdateCustomerInput,
	BillingUpdatePaymentLinkInput,
	BillingUpdateSubscriptionInput,
} from "../../types.ts";
import {
	createBillingAdminPort,
	executeAdminBillingOperation,
	type BillingAdminRuntimeOptions,
} from "../admin/execute.ts";
import { isAdminBillingOperation } from "../admin/operations.ts";
import {
	type BillingCredentialAuthority,
	readBillingCredentialAuthority,
} from "../authority.ts";
import type {
	BillingCapabilities,
	BillingOperation,
	BillingOperationCapability,
	BillingPortName,
} from "../capabilities.ts";
import {
	availableBillingCredentialEnvironments,
	type BillingCredentialKind,
	resolveBillingCredential,
} from "../credentials.ts";
import {
	type AthenaBillingRuntimeDispatch,
	invokeBillingRuntimePort,
} from "../dispatch.ts";
import { resolveBillingEnvironment } from "../environment.ts";
import type { BillingInvocationAuthority } from "../invocation-authority.ts";
import {
	isProcessBillingInvocation,
	resolveBillingInvocationAuthority,
	SESSION_BILLING_INVOCATION,
} from "../invocation-authority.ts";
import {
	callerEffectiveBillingCapability,
	denyBillingInvocation,
	PROCESS_OWNED_BILLING_PRINCIPAL,
} from "../rights.ts";
import {
	createSelfBillingPort,
	executeSelfBillingOperation,
	isSelfBillingOperation,
} from "../self/runtime.ts";
import type {
	BillingCheckoutPort,
	BillingCustomerPort,
	BillingInvoicePort,
	BillingPage,
	BillingPaymentLinkPort,
	BillingPaymentPort,
	BillingPricePort,
	BillingProductPort,
	BillingRefundPort,
	BillingSubscriptionPort,
	BillingWebhookPort,
} from "../types.ts";
import {
	decideLocalBillingOperationCapability,
	selectedModeAllowedForCapability,
} from "./capability-decision.ts";
import {
	executeLocalBillingCustomerCreate,
	executeLocalBillingCustomerDelete,
	executeLocalBillingCustomerGet,
	executeLocalBillingCustomerList,
	executeLocalBillingCustomerUpdate,
	executeLocalBillingInvoiceGet,
	executeLocalBillingInvoiceList,
	executeLocalBillingPaymentCancel,
	executeLocalBillingPaymentCreate,
	executeLocalBillingPaymentGet,
	executeLocalBillingPaymentLinkCreate,
	executeLocalBillingPaymentLinkDelete,
	executeLocalBillingPaymentLinkGet,
	executeLocalBillingPaymentLinkList,
	executeLocalBillingPaymentLinkUpdate,
	executeLocalBillingPaymentList,
	executeLocalBillingPriceList,
	executeLocalBillingProductList,
	executeLocalBillingRefundCancel,
	executeLocalBillingRefundCreate,
	executeLocalBillingRefundGet,
	executeLocalBillingRefundList,
	executeLocalBillingSubscriptionCancel,
	executeLocalBillingSubscriptionCreate,
	executeLocalBillingSubscriptionGet,
	executeLocalBillingSubscriptionList,
	executeLocalBillingSubscriptionUpdate,
} from "./execute/index.ts";
import { createConfiguredProviderBinding } from "./providers/binding.ts";
import { resolveBillingExecutionTarget } from "./providers/resolve-target.ts";
import type { BillingProviderCapabilities } from "./providers/types.ts";
import type { MaterializedBillingConnection } from "./connections/materialize.ts";

const BILLING_OPERATIONS: BillingOperation[] = [
	"payments.list",
	"payments.create",
	"payments.get",
	"payments.cancel",
	"customers.list",
	"customers.create",
	"customers.get",
	"customers.update",
	"customers.delete",
	"refunds.list",
	"refunds.create",
	"refunds.get",
	"refunds.cancel",
	"paymentLinks.list",
	"paymentLinks.create",
	"paymentLinks.get",
	"paymentLinks.update",
	"paymentLinks.delete",
	"subscriptions.list",
	"subscriptions.create",
	"subscriptions.get",
	"subscriptions.update",
	"subscriptions.cancel",
	"invoices.list",
	"invoices.get",
	"webhooks.list",
	"webhooks.create",
	"webhooks.get",
	"webhooks.update",
	"webhooks.delete",
	"webhooks.test",
	"products.list",
	"prices.list",
	"checkout.create",
	"self.invoices.list",
	"self.invoices.get",
	"self.payments.list",
	"self.payments.get",
	"self.subscription.get",
	"self.subscription.cancel",
	"self.subscription.enroll",
	"self.checkout.create",
	"self.checkout.resume",
	"admin.connections.materialize",
	"admin.reconciliation.run",
	"admin.reconciliation.retry",
	"admin.webhooks.reconcile",
	"admin.webhooks.verify",
	"admin.webhooks.status",
	"admin.ingestion.health",
	"admin.conflicts.resolve",
];

function sanitizeBillingInitializationMessage(error: unknown): string {
	const raw =
		error instanceof Error && error.message.trim().length > 0
			? error.message.trim()
			: "billing initialization failed";
	if (/access_|live_|test_|sk_|secret|password|token/i.test(raw)) {
		return "billing initialization failed";
	}
	return raw.length > 240 ? `${raw.slice(0, 237)}...` : raw;
}

function bootstrapFailedBillingCapabilities(input: {
	error: unknown;
	sqlPresent: boolean;
	target: BillingExecutionTarget;
}): BillingCapabilities {
	const operations = Object.fromEntries(
		BILLING_OPERATIONS.map((operation) => [
			operation,
			{ available: false, reason: "bootstrap_failed" as const },
		]),
	) as BillingCapabilities["operations"];
	const provider =
		typeof input.target.provider === "string" ? input.target.provider : "";
	return {
		connected: false,
		diagnostics: {
			initializationFailed: true,
			initializationMessage: sanitizeBillingInitializationMessage(input.error),
			runtimeSource: "local",
		},
		initialized: false,
		operations,
		ports: {
			checkout: false,
			customers: false,
			invoices: false,
			paymentLinks: false,
			payments: false,
			prices: false,
			products: false,
			refunds: false,
			self: input.sqlPresent,
			subscriptions: false,
			webhooks: false,
		},
		provider,
		runtime: "local",
		target: {
			kind: input.target.connectionId ? "connection" : "configured",
			provider,
			...(typeof input.target.connectionId === "string"
				? { connectionId: input.target.connectionId }
				: {}),
		},
	};
}


function omitUndefinedKeys<T>(value: T): T {
	if (value === undefined || value === null || typeof value !== "object") {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((entry) => omitUndefinedKeys(entry)) as T;
	}
	const out: Record<string, unknown> = {};
	for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
		if (entry === undefined) {
			continue;
		}
		out[key] = omitUndefinedKeys(entry);
	}
	return out as T;
}

function unavailable(
	operation: BillingOperation,
	reason: BillingOperationCapability["reason"] = "unsupported_operation",
): never {
	throw new AthenaBillingCapabilityError({
		operation,
		reason: reason ?? "unsupported_operation",
	});
}

function createUnsupportedPort<T extends object>(
	methods: Record<keyof T, BillingOperation>,
	principal: AthenaPrincipal,
	authority: BillingInvocationAuthority,
): T {
	const port = {} as T;
	for (const [method, operation] of Object.entries(methods) as Array<
		[keyof T, BillingOperation]
	>) {
		port[method] = ((..._args: unknown[]) => {
			const denied = denyBillingInvocation(principal, operation, authority);
			if (denied) {
				throw denied;
			}
			return unavailable(operation);
		}) as T[keyof T];
	}
	return port;
}


function operationsFromProviderCapabilities(input: {
	anyCredentialConfigured: boolean;
	authority?: BillingCredentialAuthority;
	configuredProfileId?: string | null;
	credentialKind?: string;
	invocation?: BillingInvocationAuthority;
	missingReason?: BillingOperationCapability["reason"];
	ports: BillingCapabilities["ports"];
	principal?: AthenaPrincipal;
	provider: string;
	providerOperations?: BillingProviderCapabilities["operations"];
	requestedProfileId?: string | null;
	selectedCredentialConfigured?: boolean;
	selectedEnvironment: "test" | "live";
	sqlPresent?: boolean;
}): BillingCapabilities["operations"] {
	const operations: BillingCapabilities["operations"] = {};
	const selectedModeAllowed = selectedModeAllowedForCapability({
		authority: input.authority,
		selectedCredentialConfigured:
			input.selectedCredentialConfigured ?? input.anyCredentialConfigured,
		selectedEnvironment: input.selectedEnvironment,
	});
	const providerConfig = {
		credentialKind: input.credentialKind,
		profileId: input.configuredProfileId,
	};
	const decideBase = {
		anyCredentialConfigured: input.anyCredentialConfigured,
		authority: input.authority,
		provider: input.provider,
		providerConfig,
		requestedProfileId: input.requestedProfileId,
		selectedModeAllowed,
	};
	for (const operation of BILLING_OPERATIONS) {
		if (
			input.missingReason != null &&
			!isSelfBillingOperation(operation) &&
			!isAdminBillingOperation(operation)
		) {
			operations[operation] = {
				available: false,
				reason: input.missingReason,
			};
			continue;
		}
		if (isAdminBillingOperation(operation)) {
			const sqlPresent = input.sqlPresent === true;
			operations[operation] = decideLocalBillingOperationCapability({
				...decideBase,
				anyCredentialConfigured: true,
				operation,
				portAvailable: sqlPresent,
				providerOperationEnabled: sqlPresent,
				unimplementedReason: "runtime_unavailable",
			});
			continue;
		}
		if (isSelfBillingOperation(operation)) {
			const sqlPresent = input.sqlPresent === true;
			if (
				operation === "self.invoices.list" ||
				operation === "self.invoices.get" ||
				operation === "self.payments.list" ||
				operation === "self.payments.get" ||
				operation === "self.subscription.get"
			) {
				operations[operation] = decideLocalBillingOperationCapability({
					...decideBase,
					anyCredentialConfigured: true,
					operation,
					portAvailable: sqlPresent,
					providerOperationEnabled: sqlPresent,
					unimplementedReason: "runtime_unavailable",
				});
			} else if (operation === "self.subscription.cancel") {
				operations[operation] = decideLocalBillingOperationCapability({
					...decideBase,
					operation,
					portAvailable: sqlPresent && input.ports.subscriptions === true,
					providerOperationEnabled:
						input.providerOperations?.["subscriptions.cancel"] === true,
					unimplementedReason: sqlPresent
						? "unsupported_operation"
						: "runtime_unavailable",
				});
			} else if (operation === "self.subscription.enroll") {
				operations[operation] = decideLocalBillingOperationCapability({
					...decideBase,
					operation,
					portAvailable:
						sqlPresent &&
						input.ports.prices === true &&
						input.ports.payments === true &&
						input.ports.subscriptions === true &&
						input.ports.customers === true,
					providerOperationEnabled:
						input.providerOperations?.["payments.create"] === true &&
						input.providerOperations?.["subscriptions.create"] === true &&
						input.providerOperations?.["customers.create"] === true,
					unimplementedReason: sqlPresent
						? "unsupported_operation"
						: "runtime_unavailable",
				});
			} else if (operation === "self.checkout.resume") {
				operations[operation] = decideLocalBillingOperationCapability({
					...decideBase,
					operation,
					portAvailable: sqlPresent && input.ports.payments === true,
					providerOperationEnabled:
						input.providerOperations?.["payments.get"] === true,
					unimplementedReason: sqlPresent
						? "unsupported_operation"
						: "runtime_unavailable",
				});
			} else {
				operations[operation] = decideLocalBillingOperationCapability({
					...decideBase,
					operation,
					portAvailable:
						sqlPresent &&
						input.ports.prices === true &&
						input.ports.payments === true,
					providerOperationEnabled:
						input.providerOperations?.["payments.create"] === true,
					unimplementedReason:
						input.ports.prices === true
							? "unsupported_operation"
							: "missing_catalog",
				});
			}
			continue;
		}
		const portName = operation.split(".")[0] as BillingPortName;
		operations[operation] = decideLocalBillingOperationCapability({
			...decideBase,
			operation,
			portAvailable: input.ports[portName] === true,
			providerOperationEnabled: input.providerOperations?.[operation] === true,
		});
	}
	if (input.principal != null) {
		for (const operation of BILLING_OPERATIONS) {
			const capability = operations[operation];
			if (capability == null) {
				continue;
			}
			operations[operation] = callerEffectiveBillingCapability({
				authority: input.invocation,
				capability,
				operation,
				principal: input.principal,
			});
		}
	}
	return operations;
}

export interface CreateLocalBillingRuntimeInput extends BillingAdminRuntimeOptions {
	connectionState?: {
		connections: readonly MaterializedBillingConnection[];
		initializationFailed?: boolean;
		initializationMessage?: string;
		initialized: boolean;
	};
	invocation: BillingInvocationAuthority;
	principal?: AthenaPrincipal;
	ready?: Promise<void>;
}

export function createLocalBillingRuntime(
	input: CreateLocalBillingRuntimeInput,
): AthenaBillingRuntimeDispatch {
	const invocation = resolveBillingInvocationAuthority({
		authority: input.invocation,
	});
	const principal =
		input.principal ??
		(isProcessBillingInvocation(invocation)
			? PROCESS_OWNED_BILLING_PRINCIPAL
			: undefined);
	if (principal == null) {
		throw new AthenaBillingCapabilityError({
			operation: "payments.list",
			reason: "runtime_unavailable",
		});
	}
	const ready = input.ready ?? Promise.resolve();
	const gate = <T>(run: () => Promise<T>): Promise<T> => ready.then(run);
	const gatePort = <T extends object>(port: T): T => {
		const wrapped = {} as T;
		for (const [key, value] of Object.entries(port)) {
			if (typeof value === "function") {
				(wrapped as Record<string, unknown>)[key] = (
					...args: unknown[]
				) =>
					gate(() =>
						(value as (...inner: unknown[]) => Promise<unknown>)(...args),
					);
				continue;
			}
			if (value != null && typeof value === "object" && !Array.isArray(value)) {
				(wrapped as Record<string, unknown>)[key] = gatePort(
					value as object,
				);
				continue;
			}
			(wrapped as Record<string, unknown>)[key] = value;
		}
		return wrapped;
	};

	const payments: BillingPaymentPort = {
		cancel: (payload: BillingCancelPaymentInput): Promise<BillingPayment> =>
			executeLocalBillingPaymentCancel({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		create: (payload: BillingCreatePaymentInput): Promise<BillingPayment> =>
			executeLocalBillingPaymentCreate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		get: (payload: BillingGetPaymentInput): Promise<BillingPayment> =>
			executeLocalBillingPaymentGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (
			payload: BillingListPaymentsInput,
		): Promise<BillingPage<BillingPayment>> =>
			executeLocalBillingPaymentList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};

	const customers: BillingCustomerPort = {
		create: (payload: BillingCreateCustomerInput) =>
			executeLocalBillingCustomerCreate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		delete: (payload: BillingDeleteCustomerInput) =>
			executeLocalBillingCustomerDelete({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		get: (payload: BillingGetCustomerInput) =>
			executeLocalBillingCustomerGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (payload: BillingListCustomersInput) =>
			executeLocalBillingCustomerList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		update: (payload: BillingUpdateCustomerInput) =>
			executeLocalBillingCustomerUpdate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const refunds: BillingRefundPort = {
		cancel: (payload: BillingCancelRefundInput) =>
			executeLocalBillingRefundCancel({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		create: (payload: BillingCreateRefundInput) =>
			executeLocalBillingRefundCreate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		get: (payload: BillingGetRefundInput) =>
			executeLocalBillingRefundGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (payload: BillingListRefundsInput) =>
			executeLocalBillingRefundList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const paymentLinks: BillingPaymentLinkPort = {
		create: (payload: BillingCreatePaymentLinkInput) =>
			executeLocalBillingPaymentLinkCreate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		delete: (payload: BillingDeletePaymentLinkInput) =>
			executeLocalBillingPaymentLinkDelete({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		get: (payload: BillingGetPaymentLinkInput) =>
			executeLocalBillingPaymentLinkGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (payload: BillingListPaymentLinksInput) =>
			executeLocalBillingPaymentLinkList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		update: (payload: BillingUpdatePaymentLinkInput) =>
			executeLocalBillingPaymentLinkUpdate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const subscriptions: BillingSubscriptionPort = {
		cancel: (payload: BillingCancelSubscriptionInput) =>
			executeLocalBillingSubscriptionCancel({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		create: (payload: BillingCreateSubscriptionInput) =>
			executeLocalBillingSubscriptionCreate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		get: (payload: BillingGetSubscriptionInput) =>
			executeLocalBillingSubscriptionGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (payload: BillingListSubscriptionsInput) =>
			executeLocalBillingSubscriptionList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		update: (payload: BillingUpdateSubscriptionInput) =>
			executeLocalBillingSubscriptionUpdate({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const invoices: BillingInvoicePort = {
		get: (payload: BillingGetInvoiceInput) =>
			executeLocalBillingInvoiceGet({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
		list: (payload: BillingListInvoicesInput) =>
			executeLocalBillingInvoiceList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const webhooks = createUnsupportedPort<BillingWebhookPort>(
		{
			create: "webhooks.create",
			delete: "webhooks.delete",
			get: "webhooks.get",
			list: "webhooks.list",
			test: "webhooks.test",
			update: "webhooks.update",
		},
		principal,
		invocation,
	);
	const products: BillingProductPort = {
		list: (payload) =>
			executeLocalBillingProductList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const prices: BillingPricePort = {
		list: (payload) =>
			executeLocalBillingPriceList({
				authority: invocation,
				configuredProviders: input.configuredProviders,
				payload,
				principal,
				registry: input.registry,
				testMode: input.testMode,
			}),
	};
	const checkout = createUnsupportedPort<BillingCheckoutPort>(
		{ create: "checkout.create" },
		principal,
		invocation,
	);
	const adminOptions = {
		applicationId: input.applicationId,
		appUrl: input.appUrl,
		configuredProviders: input.configuredProviders,
		customerImport: input.customerImport,
		ingestion: input.ingestion,
		observability: input.observability,
		registry: input.registry,
		sql: input.sql,
		testMode: input.testMode,
	};
	const admin = createBillingAdminPort({
		authority: invocation,
		options: adminOptions,
		principal,
	});

	return {
		// Shared src/billing/safety preflight is applied in execute/* create paths.
		customers: gatePort(customers),
		admin: gatePort(admin),
		checkout: gatePort(checkout),
		async execute(operation, payload, requestPrincipal) {
			await ready;
			if (isSelfBillingOperation(operation)) {
				return omitUndefinedKeys(
					await executeSelfBillingOperation({
						appUrl: input.appUrl,
						configuredProviders: input.configuredProviders,
						operation: operation,
						payload,
						principal: requestPrincipal,
						registry: input.registry,
						sql: input.sql,
						testMode: input.testMode,
					}),
				);
			}
			if (isAdminBillingOperation(operation)) {
				return omitUndefinedKeys(
					await executeAdminBillingOperation({
						authority: invocation,
						operation,
						options: adminOptions,
						payload,
						principal: requestPrincipal,
					}),
				);
			}
			const scoped = createLocalBillingRuntime({
				applicationId: input.applicationId,
				appUrl: input.appUrl,
				configuredProviders: input.configuredProviders,
				connectionState: input.connectionState,
				customerImport: input.customerImport,
				ingestion: input.ingestion,
				invocation: SESSION_BILLING_INVOCATION,
				observability: input.observability,
				principal: requestPrincipal,
				ready,
				registry: input.registry,
				sql: input.sql,
				testMode: input.testMode,
			});
			return omitUndefinedKeys(
				await invokeBillingRuntimePort(scoped, operation, payload),
			);
		},
		invoices: gatePort(invoices),
		mode: "local",
		paymentLinks: gatePort(paymentLinks),
		payments: gatePort(payments),
		prices: gatePort(prices),
		products: gatePort(products),
		refunds: gatePort(refunds),
		self: gatePort(
			createSelfBillingPort({
				appUrl: input.appUrl,
				configuredProviders: input.configuredProviders,
				principal,
				registry: input.registry,
				sql: input.sql,
				testMode: input.testMode,
			}),
		),
		subscriptions: gatePort(subscriptions),
		webhooks: gatePort(webhooks),
		async getCapabilities(
			target: BillingExecutionTarget,
		): Promise<BillingCapabilities> {
			if (input.connectionState?.initializationFailed) {
				return bootstrapFailedBillingCapabilities({
					error:
						input.connectionState.initializationMessage ??
						"billing initialization failed",
					sqlPresent: input.sql != null,
					target,
				});
			}
			try {
				await ready;
			} catch (error) {
				return bootstrapFailedBillingCapabilities({
					error,
					sqlPresent: input.sql != null,
					target,
				});
			}
			const resolved = resolveBillingExecutionTarget({
				configuredProviders: input.configuredProviders,
				registry: input.registry,
				target,
			});
			if (resolved.kind === "connection") {
				const emptyPorts = {
					checkout: false,
					customers: false,
					invoices: false,
					paymentLinks: false,
					payments: false,
					prices: false,
					products: false,
					refunds: false,
					self: input.sql != null,
					subscriptions: false,
					webhooks: false,
				};
				return {
					connected: false,
					connectionId: resolved.connectionId,
					initialized: input.connectionState?.initialized !== false,
					operations: operationsFromProviderCapabilities({
						anyCredentialConfigured: false,
						invocation,
						missingReason: "missing_connection",
						ports: emptyPorts,
						principal,
						provider: resolved.provider ?? "",
						selectedEnvironment: "test",
						sqlPresent: input.sql != null,
					}),
					ports: emptyPorts,
					provider: resolved.provider ?? "",
					runtime: "local",
					target: {
						connectionId: resolved.connectionId,
						kind: "connection",
						provider: resolved.provider ?? "",
					},
				};
			}
			const environment = resolveBillingEnvironment({
				testMode: input.testMode,
			});
			const binding = createConfiguredProviderBinding({
				configuredProviders: input.configuredProviders,
				provider: resolved.provider,
			});
			const providerCapabilities =
				await resolved.runtime.getCapabilities(binding);
			const providerPorts = providerCapabilities.ports;
			const ports = {
				checkout: resolved.runtime.checkout != null && providerPorts.checkout,
				customers:
					resolved.runtime.customers != null && providerPorts.customers,
				invoices: resolved.runtime.invoices != null && providerPorts.invoices,
				paymentLinks:
					resolved.runtime.paymentLinks != null && providerPorts.paymentLinks,
				payments: resolved.runtime.payments != null && providerPorts.payments,
				prices: resolved.runtime.prices != null && providerPorts.prices,
				products: resolved.runtime.products != null && providerPorts.products,
				refunds: resolved.runtime.refunds != null && providerPorts.refunds,
				self: input.sql != null,
				subscriptions:
					resolved.runtime.subscriptions != null && providerPorts.subscriptions,
				webhooks: resolved.runtime.webhooks != null && providerPorts.webhooks,
			};
			const availableEnvironments = availableBillingCredentialEnvironments(
				binding.credentials,
			);
			const authority = readBillingCredentialAuthority(binding.providerConfig);
			let credentialKind: BillingCredentialKind | undefined =
				typeof binding.providerConfig.credentialKind === "string"
					? (binding.providerConfig.credentialKind as BillingCredentialKind)
					: undefined;
			let credentialAvailable = false;
			try {
				const selection = resolveBillingCredential({
					provider: resolved.provider,
					testMode: environment.testMode,
					binding,
				});
				credentialKind = selection.credentialKind;
				credentialAvailable = true;
			} catch (error) {
				if (!isAthenaBillingCredentialError(error)) {
					throw error;
				}
				credentialAvailable = false;
			}
			const configuredProfileId = binding.providerConfig.profileId;
			const operations = operationsFromProviderCapabilities({
				anyCredentialConfigured: availableEnvironments.length > 0,
				authority,
				configuredProfileId:
					typeof configuredProfileId === "string" ||
						configuredProfileId === null
						? configuredProfileId
						: undefined,
				credentialKind,
				invocation,
				ports,
				principal,
				provider: resolved.provider,
				providerOperations: providerCapabilities.operations,
				requestedProfileId: target.profileId,
				selectedCredentialConfigured:
					binding.credentials[environment.name] != null,
				selectedEnvironment: environment.name,
				sqlPresent: input.sql != null,
			});
			const materialized = input.connectionState?.connections.find(
				(connection) => connection.provider === resolved.provider,
			);
			const developmentDiagnostics =
				process.env.NODE_ENV !== "production"
					? {
							connectionSource: "application_config",
							providerRegistrationSource: "createClient",
							runtimeSource: "local",
						}
					: undefined;
			return {
				authority:
					authority == null
						? undefined
						: {
							modes: authority.modes,
							permissions: { ...authority.permissions },
							scope: authority.scope,
							source: authority.source,
						},
				connected: credentialAvailable,
				connectionId: materialized?.id,
				credentials: {
					availableEnvironments,
					configured: true,
					credentialKind,
					selectedEnvironment: environment.name,
				},
				...(developmentDiagnostics != null
					? { diagnostics: developmentDiagnostics }
					: {}),
				environment: environment.name,
				initialized: input.connectionState?.initialized !== false,
				operations,
				ports,
				provider: resolved.provider,
				runtime: "local",
				target: {
					kind: "configured",
					provider: resolved.provider,
					...(materialized?.id != null
						? { connectionId: materialized.id }
						: {}),
				},
				testMode: environment.testMode,
			};
		},
	};
}
